import "./i18n";
import { Switch, Route, Router as WouterRouter, useLocation } from "wouter";
import { HelmetProvider } from "react-helmet-async";
import { lazy, Suspense, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { AnimatePresence, motion } from "framer-motion";
import { useTheme } from "./hooks/useTheme";
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
// import SocialFloat from "./components/SocialFloat";
import ChatBot from "./components/ChatBot";
import ScrollProgress from "./components/ScrollProgress";
import CustomCursor from "./components/CustomCursor";
import ParticleField from "./components/ParticleField";

// Home is eager-loaded (above-the-fold) — every other route is split into its own chunk
import Home from "./pages/Home";

// Public routes
const Entreprise = lazy(() => import("./pages/Entreprise"));
const Formations = lazy(() => import("./pages/Formations"));
const Ecole = lazy(() => import("./pages/Ecole"));
const Vision = lazy(() => import("./pages/Vision"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogDetail = lazy(() => import("./pages/BlogDetail"));
const Contact = lazy(() => import("./pages/Contact"));
const FAQ = lazy(() => import("./pages/FAQ"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Admin routes
const AdminLogin = lazy(() => import("./pages/Admin/Login"));
const Dashboard = lazy(() => import("./pages/Admin/Dashboard"));
const BlogList = lazy(() => import("./pages/Admin/BlogList"));
const BlogForm = lazy(() => import("./pages/Admin/BlogForm"));
const FormsList = lazy(() => import("./pages/Admin/FormsList"));

const GOLD = "#C9A84C";

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center" role="status" aria-live="polite">
      <span className="sr-only">Chargement…</span>
      <div
        className="w-10 h-10 rounded-full border-2 animate-spin"
        style={{ borderColor: `${GOLD}33`, borderTopColor: GOLD }}
      />
    </div>
  );
}

function AppContent() {
  const { i18n } = useTranslation();
  useTheme();
  const [location] = useLocation();

  const lang = i18n.language;
  const seoTitles: Record<string, string> = {
    fr: "MindBridge | Révolutionner la Formation avec l'IA, la Psychométrie et les Neurosciences",
    ar: "MindBridge | ثورة في التدريب بالذكاء الاصطناعي وعلم القياس النفسي وعلوم الأعصاب",
    en: "MindBridge | Revolutionizing Training with AI, Psychometrics and Neuroscience",
  };
  const seoDescriptions: Record<string, string> = {
    fr: "MindBridge est une startup EdTech innovante spécialisée en IA, psychométrie et neurosciences pour la formation des entreprises, écoles et organismes professionnels.",
    ar: "MindBridge — شركة ناشئة في تكنولوجيا التعليم تجمع الذكاء الاصطناعي وعلم القياس النفسي وعلوم الأعصاب لتحويل التدريب.",
    en: "MindBridge is an innovative EdTech startup combining AI, psychometrics and neuroscience to transform corporate training, schools and professional education.",
  };

  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
  }, [lang]);

  return (
    <>
      <Helmet>
        <title>{seoTitles[lang] ?? seoTitles.fr}</title>
        <meta name="description" content={seoDescriptions[lang] ?? seoDescriptions.fr} />
        <meta property="og:title" content={seoTitles[lang] ?? seoTitles.fr} />
        <meta property="og:description" content={seoDescriptions[lang] ?? seoDescriptions.fr} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <CustomCursor />
      <ParticleField />
      <ScrollProgress />
      <div className="flex flex-col min-h-screen">
        <Navbar />
        {/* <SocialFloat /> */}
        <main className="flex-1">
          <AnimatePresence mode="wait">
            <motion.div
              key={location}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.32, ease: "easeInOut" }}
            >
              <Suspense fallback={<PageLoader />}>
                <Switch>
                  <Route path="/" component={Home} />
                  <Route path="/entreprise" component={Entreprise} />
                  <Route path="/formations" component={Formations} />
                  <Route path="/ecole" component={Ecole} />
                  <Route path="/vision" component={Vision} />
                  <Route path="/blog/:id" component={BlogDetail} />
                  <Route path="/blog" component={Blog} />
                  <Route path="/contact" component={Contact} />
                  <Route path="/faq" component={FAQ} />
                  <Route component={NotFound} />
                </Switch>
              </Suspense>
            </motion.div>
          </AnimatePresence>
        </main>
        <Footer />
        <ChatBot />
      </div>
    </>
  );
}

function RootRouter() {
  const [location] = useLocation();
  const isAdmin = location.startsWith("/admin");

  if (isAdmin) {
    return (
      <Suspense fallback={<PageLoader />}>
        <Switch>
          <Route path="/admin/login" component={AdminLogin} />
          <Route path="/admin/blog/new" component={BlogForm} />
          <Route path="/admin/blog/:id/edit" component={BlogForm} />
          <Route path="/admin/blog" component={BlogList} />
          <Route path="/admin/forms" component={FormsList} />
          <Route path="/admin" component={Dashboard} />
          <Route path="/admin/dashboard" component={Dashboard} />
        </Switch>
      </Suspense>
    );
  }

  return <AppContent />;
}

function App() {
  return (
    <HelmetProvider>
      <WouterRouter base={import.meta.env.BASE_URL.replace(/\/$/, "")}>
        <RootRouter />
      </WouterRouter>
    </HelmetProvider>
  );
}

export default App;
